import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { KTIcon } from '../../../_metronic/helpers'
import { Tr } from '../../../_metronic/i18n/Tr'
import { ToastContainer } from 'react-toastify'
import DataTable from 'react-data-table-component'
import Select from 'react-select'
import KpiCard from './components/KpiCard'
import { selectStyles } from './components/selectStyles'
import { formatNumber } from '../../../reusable'
import { useDemographics, useCountryList, useRegionList, type DemoSummary } from './useDemographics'

// ─── Filter options ───
const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
]

const AGE_OPTIONS = [
  { value: '18-25', label: '18 - 25' },
  { value: '26-35', label: '26 - 35' },
  { value: '36-45', label: '36 - 45' },
  { value: '46-55', label: '46 - 55' },
  { value: '55+', label: '55+' },
]

const DEVICE_OPTIONS = [
  { value: 'android', label: 'Android' },
  { value: 'ios', label: 'iOS' },
]

// ─── Filter Bar ───
const FilterBar = React.memo<{
  filters: any
  countryList: any[]
  regionList: any[]
  onFilterChange: (key: string, val: any) => void
  onClear: () => void
  t: (key: string) => string
}>(({ filters, countryList, regionList, onFilterChange, onClear, t }) => {
  const hasFilters = filters.country || filters.region || filters.gender || filters.ageRange || filters.device

  return (
    <div className="card border-0 shadow-sm mb-6">
      <div className="card-body py-5 px-6">
        <div className="d-flex flex-wrap align-items-center gap-3">
          {/* Country */}
          <div style={{ minWidth: 180 }}>
            <Select options={countryList} value={filters.country} onChange={(v: any) => onFilterChange('country', v)} isClearable placeholder={t('Country')} styles={selectStyles} />
          </div>

          {/* Region */}
          <div style={{ minWidth: 180 }}>
            <Select options={regionList} value={filters.region} onChange={(v: any) => onFilterChange('region', v)} isClearable placeholder={t('Region')} styles={selectStyles} />
          </div>

          {/* Gender */}
          <div style={{ minWidth: 140 }}>
            <Select options={GENDER_OPTIONS} value={filters.gender} onChange={(v: any) => onFilterChange('gender', v)} isClearable placeholder={t('Gender')} styles={selectStyles} />
          </div>

          {/* Age */}
          <div style={{ minWidth: 140 }}>
            <Select options={AGE_OPTIONS} value={filters.ageRange} onChange={(v: any) => onFilterChange('ageRange', v)} isClearable placeholder={t('Age')} styles={selectStyles} />
          </div>

          {/* Device */}
          <div style={{ minWidth: 140 }}>
            <Select options={DEVICE_OPTIONS} value={filters.device} onChange={(v: any) => onFilterChange('device', v)} isClearable placeholder={t('Device')} styles={selectStyles} />
          </div>

          {hasFilters ? (
            <button className="btn btn-sm btn-light-danger fs-7" onClick={onClear}>
              <KTIcon iconName="cross" className="fs-5 me-1" />
              {t('Clear All')}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  )
})

// ─── Summary Section ───
const SummarySection: FC<{ summary: DemoSummary;  filters: any ; t: (key: string) => string }> = React.memo(({ summary, filters, t }) => (
  <div className="row g-4 mb-6">
    <KpiCard title={t('Total Guests')} value={(summary?.totalGuests ?? 0).toLocaleString()} color="primary" icon="people" />
    <KpiCard title={t('Total Bookings')} value={(summary?.totalBookings ?? 0).toLocaleString()} color="info" icon="chart-simple" />
  {filters.country || filters.region ? null :
      <KpiCard title={t('Top Country')} value={typeof summary?.topCountry === 'object' ? summary?.topCountry?.country ?? '-' : summary?.topCountry ?? '-'} color="success" icon="geolocation" />
  }
    <KpiCard title={t('Repeat Guests')} value={(summary?.repeatGuests ?? 0).toLocaleString()} color="warning" icon="people" />
  </div>
))

// ─── Main Page ───
const Demographics: FC = () => {
  const tFn = Tr()
  const tRef = useRef(tFn)
  tRef.current = tFn
  const t = useCallback((key: string) => tRef.current(key), [])
  const countryList = useCountryList()
  const regionList = useRegionList()

  const [filters, setFilters] = useState({
    dateRange: [] as Date[],
    category: null as any,
    country: null as any,
    region: null as any,
    gender: null as any,
    ageRange: null as any,
    device: null as any,
  })

  const { summary, bookings, totalRecords, isLoading, limit, handlePageChange, handleRowsPerPageChange } = useDemographics(filters)

  useEffect(() => {
    document.title = 'Insayr | Demographics'
  }, [])

  const handleFilterChange = useCallback((key: string, val: any) => {
    setFilters(prev => ({ ...prev, [key]: val }))
  }, [])

  const handleClear = useCallback(() => {
    setFilters({
      dateRange: [],
      category: null,
      country: null,
      region: null,
      gender: null,
      ageRange: null,
      device: null,
    })
  }, [])

  // ─── Table Columns ───
  const columns = useMemo(() => [
    {
      name: t('Guest'),
      cell: (row: any) => (
        <div className="py-2">
          <div className="fw-bold text-gray-900 fs-6">{row?.guestName ?? '-'}</div>
          <div className="text-gray-500 fs-8">{row?.email ?? ''}</div>
        </div>
      ),
      grow: 2,
    },
    {
      name: t('Gender'),
      cell: (row: any) => <span className="fw-semibold text-gray-800 fs-7 text-capitalize">{(row?.gender ?? '-').toLowerCase()}</span>,
      width: '90px',
    },
    {
      name: t('Age'),
      cell: (row: any) => <span className="fw-semibold text-gray-800 fs-7">{row?.age ?? '-'}</span>,
      width: '70px',
    },
    {
      name: t('Country'),
      cell: (row: any) => <span className="text-gray-700 fs-7">{row?.country ?? '-'}</span>,
      width: '120px',
    },
    {
      name: t('Region'),
      cell: (row: any) => <span className="text-gray-700 fs-7">{row?.region || '-'}</span>,
      width: '120px',
    },
    {
      name: t('Device'),
      cell: (row: any) => {
        const d = (row?.device ?? '').toLowerCase()
        const colorMap: Record<string, string> = { android: 'success', ios: 'info', web: 'primary' }
        return <span className={`badge badge-light-${colorMap[d] ?? 'dark'} fs-8 fw-semibold text-capitalize`}>{d || '-'}</span>
      },
      width: '100px',
    },
    {
      name: t('Bookings'),
      cell: (row: any) => <span className="fw-bold text-gray-900 fs-7">{row?.totalBookings ?? 0}</span>,
      width: '100px',
    },
    {
      name: t('Total Spent'),
      cell: (row: any) => <span className="fw-bold text-gray-900 fs-7">{formatNumber(row?.totalAmount)}</span>,
      width: '140px',
    },
    {
      name: t('Last Booking'),
      cell: (row: any) => <span className="fw-semibold text-gray-800 fs-7">{row?.lastBookingDate ?? '-'}</span>,
      width: '130px',
    },
  ], [t])

  return (
    <>
      <ToastContainer />

      <FilterBar
        filters={filters}
        countryList={countryList}
        regionList={regionList}
        onFilterChange={handleFilterChange}
        onClear={handleClear}
        t={t}
      />

    <SummarySection summary={summary} filters={filters} t={t} />

      {/* Bookings Table */}
      <div className="card border-0 shadow-sm">
        <div className="card-header border-0 pt-6">
          <h3 className="card-title fw-bold text-gray-900 fs-5">{t('Guest Bookings')}</h3>
          <div className="card-toolbar">
            <span className="text-gray-500 fs-7">{totalRecords ?? 0} {t('records')}</span>
          </div>
        </div>
        <div className="card-body pt-0">
          <DataTable
            columns={columns}
            data={bookings ?? []}
            pagination
            paginationServer
            paginationTotalRows={totalRecords}
            paginationPerPage={limit}
            paginationRowsPerPageOptions={[5, 10, 20, 30]}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handleRowsPerPageChange}
            progressPending={isLoading}
            progressComponent={
              <div className="py-10 text-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">{t('Loading...')}</span>
                </div>
              </div>
            }
            highlightOnHover
            noDataComponent={
              <div className="text-center py-10">
                <KTIcon iconName="people" className="fs-2tx text-gray-300 mb-5" />
                <div className="text-gray-500 fs-5">{t('No bookings found')}</div>
              </div>
            }
            paginationComponentOptions={{ rowsPerPageText: t('Rows Per Page') }}
          />
        </div>
      </div>
    </>
  )
}

export default Demographics
